import { useEffect, useRef, useState } from 'react';
import { FiX, FiCheckCircle } from 'react-icons/fi';
import api from '../../utils/api';

// Two separate localStorage keys (instead of one JSON blob) so each
// signal can be reasoned about and cleared independently:
//   petzy_newsletter_subscribed    = "true" once the user subscribes
//   petzy_newsletter_dismissed_at  = timestamp (ms) of last dismissal
const STORAGE_SUBSCRIBED   = 'petzy_newsletter_subscribed';
const STORAGE_DISMISSED_AT = 'petzy_newsletter_dismissed_at';

// Frequency cap on dismiss — don't nag the same visitor again for a week.
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// Fallback trigger for visitors who stay on the page without scrolling
// or attempting to leave. ~20s matches the "user has settled in" window.
const TIME_DELAY_MS = 20 * 1000;
// Scroll ratio at which we consider the page "consumed".
const SCROLL_THRESHOLD = 0.7;
// Minimal email sanity check — the backend runs the authoritative check,
// this just stops an obviously invalid submit from round-tripping.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function shouldShow() {
  try {
    if (localStorage.getItem(STORAGE_SUBSCRIBED) === 'true') return false;
    const raw = localStorage.getItem(STORAGE_DISMISSED_AT);
    const at = raw ? parseInt(raw, 10) : 0;
    if (at && Date.now() - at < DISMISS_COOLDOWN_MS) return false;
    return true;
  } catch {
    // localStorage can throw (private mode, quota, disabled cookies) —
    // fail open: show the popup rather than silently vanish it.
    return true;
  }
}

function isFinePointer() {
  // `(pointer: fine)` is true only for devices with a precise pointer
  // (desktop mouse, stylus) — false for touch. This is the modern,
  // matchMedia-native way to detect "can do hover/exit-intent".
  try { return window.matchMedia('(pointer: fine)').matches; }
  catch { return false; }
}

export default function NewsletterPopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Ref (not state) so the trigger handlers can read the "already
  // triggered" flag synchronously without closing over stale state.
  const hasTriggeredRef = useRef(false);

  // Smart-triggers effect — runs once on mount. Installs three race
  // conditions (exit-intent, scroll depth, time delay); whichever
  // fires first wins, and then they're all torn down together.
  useEffect(() => {
    if (!shouldShow()) return;

    const trigger = () => {
      if (hasTriggeredRef.current) return;
      hasTriggeredRef.current = true;
      setVisible(true);
    };

    // Time-delay fallback.
    const timer = setTimeout(trigger, TIME_DELAY_MS);

    // Scroll depth. Skip if the page isn't actually scrollable —
    // otherwise 70% is always satisfied and the popup fires instantly.
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const ratio = (window.scrollY + window.innerHeight) / doc.scrollHeight;
      if (ratio >= SCROLL_THRESHOLD) trigger();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Exit intent — desktop only (no mouse = no meaningful "mouse left").
    // Capture finePointer once at mount so the cleanup tears down the
    // same listener it registered; a runtime change of matchMedia would
    // otherwise leak the listener.
    const finePointer = isFinePointer();
    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };
    if (finePointer) {
      document.addEventListener('mouseleave', onMouseLeave);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      if (finePointer) document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  // ESC to dismiss — only armed while the modal is open.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Body scroll lock while the modal is open — prevents background
  // pages from scrolling under the overlay on mobile sheet layouts.
  //
  // When overflow:hidden hides the scrollbar the viewport widens by
  // the scrollbar's pixel width, shifting page content right. We
  // measure the gap before locking and ADD the same amount on top of
  // whatever padding-right the body already has (e.g. set by a
  // stylesheet, by an extension's content script, or by a sibling
  // modal that already locked) so the layout stays put without
  // clobbering existing padding. The cleanup still restores the raw
  // inline value we captured, so we don't accidentally persist the
  // computed sum after the modal closes.
  useEffect(() => {
    if (!visible) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const existing = parseInt(
        window.getComputedStyle(document.body).paddingRight,
        10,
      ) || 0;
      document.body.style.paddingRight = `${existing + scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadding;
    };
  }, [visible]);

  // Auto-close the success state after 4 s. Declarative: the timer
  // exists exactly while `success && visible` both hold, and the
  // cleanup runs when either flips (manual dismiss, unmount, success
  // reset). 4 s gives a screen reader room to finish announcing
  // both the heading ("Te-ai înscris cu succes!") and the follow-up
  // ("Îți mulțumim — te vom anunța când apar noutăți.") via the
  // live region below, before the modal unmounts and the queued
  // announcement gets dropped. Also lines up with the react-hot-
  // toast default for success notifications.
  useEffect(() => {
    if (!success || !visible) return undefined;
    const timer = setTimeout(() => setVisible(false), 4000);
    return () => clearTimeout(timer);
  }, [success, visible]);

  const handleClose = () => {
    setVisible(false);
    // Only stamp "dismissed" when the user actually backed out. On the
    // success path we already wrote the `subscribed` flag, which takes
    // precedence in shouldShow() regardless of the dismiss timestamp.
    if (!success) {
      try { localStorage.setItem(STORAGE_DISMISSED_AT, String(Date.now())); }
      catch { /* noop */ }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (trimmedName.length < 2) {
      setError('Te rugăm să completezi numele.');
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError('Adresa de email nu pare validă.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/platform/subscribe', {
        email: trimmedEmail,
        name: trimmedName,
        source: 'PLATFORM_POPUP',
      });
      try { localStorage.setItem(STORAGE_SUBSCRIBED, 'true'); }
      catch { /* noop */ }
      // Auto-close is scheduled by the [success, visible] effect
      // above. That effect's cleanup handles manual dismiss + unmount
      // + a race where the request resolves AFTER the component has
      // been torn down (the effect simply never runs).
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.message || 'A apărut o eroare. Încearcă din nou.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 pb-4 sm:pb-0 animate-[fadeIn_0.2s_ease-out]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nl-popup-title"
      onClick={handleClose}
    >
      <div
        className="relative bg-cream-50 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-[420px] p-8 animate-[slideUp_0.25s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Discreet close button — premium minimal, neutral palette.
            p-3.5 (14 px) + w-4 h-4 (16 px) = exact 44×44 tap target per
            WCAG 2.1 SC 2.5.5 AAA / 2.2 AA. Offset top-2.5 right-2.5
            (10 px) + 14 px padding + 8 px icon-half = 32 px from corner
            to icon centre — same visual placement as the previous
            top-3 right-3 + p-3 combo, just a wider hit area. This
            popup uses the mobile-sheet layout (`items-end
            sm:items-center`), so on phones the close button is the
            primary "out" and worth hitting the full 44 px target. The
            other modal close buttons in the app stay at p-2 — they're
            centred-desktop modals and don't have the same primary-out
            semantic. */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-2.5 right-2.5 p-3.5 rounded-full text-ink-400 hover:bg-ink/5 hover:text-ink-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          aria-label="Închide"
        >
          <FiX className="w-4 h-4" aria-hidden="true" />
        </button>

        {success ? (
          // aria-live + aria-atomic on the outer wrapper so the screen
          // reader announces the heading AND the follow-up text as a
          // single unit when the success branch mounts. The h2 alone
          // wouldn't cover the <p>; aria-atomic ensures both lines
          // read together rather than just the changed node.
          // id="nl-popup-title" stays on the h2 — that's the dialog's
          // aria-labelledby target across the form/success state swap.
          // FiCheckCircle is aria-hidden, so it sits inside the live
          // region without adding noise to the announcement.
          <div
            className="text-center py-4"
            aria-live="polite"
            aria-atomic="true"
          >
            <FiCheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" aria-hidden="true" />
            <h2
              id="nl-popup-title"
              className="font-display text-lg font-bold text-ink mb-1"
            >
              Te-ai înscris cu succes!
            </h2>
            <p className="text-sm text-ink-500">Îți mulțumim — te vom anunța când apar noutăți.</p>
          </div>
        ) : (
          <>
            <div className="text-center mt-2">
              <h2 id="nl-popup-title" className="font-display text-xl font-bold text-ink tracking-tight leading-snug">
                Primește oferte și noutăți Petzy
              </h2>
              <p className="text-sm text-ink-500 mt-2 leading-relaxed">
                Reduceri, saloane noi și sfaturi utile pentru animalul tău — trimise direct pe email.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-3" noValidate>
              {/* Nume — Airbnb-style floating label. The peer trick swaps
                  label position based on whether the input shows the
                  placeholder (i.e. is empty) and whether it has focus. */}
              <div className="relative">
                <input
                  type="text"
                  id="newsletter-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nume"
                  required
                  minLength={2}
                  maxLength={120}
                  className="peer w-full px-4 pt-5 pb-1.5 border border-ink/15 rounded-xl text-sm text-ink placeholder-transparent focus:outline-none focus:border-ink focus:ring-0 transition"
                />
                <label
                  htmlFor="newsletter-name"
                  className="absolute left-4 top-1.5 text-[10px] font-bold text-ink-400 uppercase tracking-wider transition-all
                             peer-placeholder-shown:text-sm peer-placeholder-shown:top-3.5 peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal
                             peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:font-bold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-ink-600"
                >
                  Nume
                </label>
              </div>

              <div className="relative">
                <input
                  type="email"
                  id="newsletter-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Adresă email"
                  required
                  maxLength={180}
                  className="peer w-full px-4 pt-5 pb-1.5 border border-ink/15 rounded-xl text-sm text-ink placeholder-transparent focus:outline-none focus:border-ink focus:ring-0 transition"
                />
                <label
                  htmlFor="newsletter-email"
                  className="absolute left-4 top-1.5 text-[10px] font-bold text-ink-400 uppercase tracking-wider transition-all
                             peer-placeholder-shown:text-sm peer-placeholder-shown:top-3.5 peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal
                             peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:font-bold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-ink-600"
                >
                  Adresă email
                </label>
              </div>

              {error && (
                <p className="text-sm text-red-600" role="alert">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-2 py-3 bg-primary-500 hover:bg-primary-600 active:scale-[0.98] text-white font-bold rounded-xl transition duration-150 shadow-sm disabled:opacity-50 disabled:active:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                {submitting ? 'Te înscrii…' : 'Mă înscriu'}
              </button>
            </form>

            <p className="text-[11px] text-ink-500 text-center mt-4 leading-normal">
              Te poți dezabona oricând. Nu trimitem spam.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
